<script setup lang="ts">
/**
 * A chart's figures and how they are drawn: the fields both the chart section
 * and a chart placed freely on a page are edited with.
 *
 * The figures are edited as a grid with the labels across the top and one row
 * per series, which is how the data is actually shaped and how the chart
 * reads. A list of points would make it impossible to see that one series is
 * a value short, which is the mistake that silently shifts every figure onto
 * the wrong label. Adding a label adds a value to every series, and removing
 * one removes it everywhere, so the rows can never end up ragged.
 *
 * Each series carries a colour, and so does each label when the labels are
 * what the colours tell apart (a pie's slices, or the bars of one series
 * coloured each its own). Unset, a colour is a shade of the document's accent.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import {
  CHART_ORIENTATIONS,
  CHART_TYPES,
  LEGEND_POSITIONS,
  chartHouse,
  chartPalette,
  coloursByLabel,
  figureOf,
  labelColour,
  seriesColour,
  type ChartContent,
} from '@/domain/chart'
import { newSeries } from '@/domain/section'
import InspectorField from '@/sections/InspectorField.vue'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()

const props = defineProps<{
  chart: ChartContent
  /**
   * Which of the fields: the figures (`data`: the type, the grid, the
   * colours) or how they are drawn (`look`). Both when not said.
   */
  part?: 'data' | 'look' | undefined
}>()

function shows(part: 'data' | 'look'): boolean {
  return props.part === undefined || props.part === part
}
const emit = defineEmits<{ change: [changes: Partial<ChartContent>] }>()

const report = useReportStore()

const palette = computed(() => chartPalette(report.layout.accent, chartHouse(report.layout)))
const columns = computed(() => props.chart.labels.length)
const round = computed(
  () => props.chart.chartType === 'pie' || props.chart.chartType === 'doughnut',
)
const bars = computed(() => props.chart.chartType === 'bar')
const byLabel = computed(() => coloursByLabel(props.chart))
/** Only one series can have its bars coloured each its own: several are told apart by theirs. */
const canColourLabels = computed(() => !round.value && props.chart.series.length === 1)
const customised = computed(
  () => props.chart.series.some((one) => one.colour) || props.chart.labelColours.some(Boolean),
)

function set(changes: Partial<ChartContent>): void {
  emit('change', changes)
}

function setLabel(at: number, value: string): void {
  set({ labels: props.chart.labels.map((label, index) => (index === at ? value : label)) })
}

function setSeries(id: string, changes: { label?: string; colour?: string }): void {
  set({ series: props.chart.series.map((one) => (one.id === id ? { ...one, ...changes } : one)) })
}

/** A figure, or zero. A blank cell is a gap in the data, and gaps read as zero. */
function setValue(id: string, at: number, typed: string): void {
  const value = figureOf(typed)
  set({
    series: props.chart.series.map((one) =>
      one.id === id
        ? { ...one, values: one.values.map((held, index) => (index === at ? value : held)) }
        : one,
    ),
  })
}

function setLabelColour(at: number, colour: string): void {
  const colours = props.chart.labels.map((_, index) => props.chart.labelColours[index] ?? '')
  colours[at] = colour.toLowerCase()
  set({ labelColours: colours })
}

/** Every bar of the one series in a colour of its own, starting from the palette's shades. */
function colourEachLabel(on: boolean): void {
  set({
    labelColours: on
      ? props.chart.labels.map((_, at) => labelColour(props.chart, at, palette.value))
      : [],
  })
}

function resetColours(): void {
  set({
    series: props.chart.series.map((one) => ({ ...one, colour: '' })),
    labelColours: [],
  })
}

/** A label and its value in every series, together, so nothing goes ragged. */
function addLabel(): void {
  set({
    labels: [...props.chart.labels, ''],
    series: props.chart.series.map((one) => ({ ...one, values: [...one.values, 0] })),
  })
}

function removeLabel(at: number): void {
  if (columns.value <= 1) return
  set({
    labels: props.chart.labels.filter((_, index) => index !== at),
    series: props.chart.series.map((one) => ({
      ...one,
      values: one.values.filter((_, index) => index !== at),
    })),
    labelColours: props.chart.labelColours.filter((_, index) => index !== at),
  })
}

function addSeries(): void {
  const blank = newSeries()
  set({
    series: [...props.chart.series, { ...blank, values: props.chart.labels.map(() => 0) }],
  })
}

function removeSeries(id: string): void {
  if (props.chart.series.length <= 1) return
  set({ series: props.chart.series.filter((one) => one.id !== id) })
}

function checked(event: Event): boolean {
  return (event.target as HTMLInputElement).checked
}

function typed(event: Event): string {
  return (event.target as HTMLInputElement).value
}
</script>

<template>
  <div class="fields">
    <InspectorField v-if="shows('data')" :label="t('inspector.chart.type')">
      <div class="options options--4" role="group" :aria-label="t('inspector.chart.type')">
        <button
          v-for="type in CHART_TYPES"
          :key="type"
          type="button"
          class="options__option"
          :class="{ 'options__option--on': chart.chartType === type }"
          :aria-pressed="chart.chartType === type"
          @click="set({ chartType: type })"
        >
          {{ t(`inspector.chart.types.${type}`) }}
        </button>
      </div>
    </InspectorField>

    <InspectorField v-if="bars && shows('look')" :label="t('inspector.chart.orientation')">
      <div class="options options--2" role="group" :aria-label="t('inspector.chart.orientation')">
        <button
          v-for="orientation in CHART_ORIENTATIONS"
          :key="orientation"
          type="button"
          class="options__option"
          :class="{ 'options__option--on': chart.orientation === orientation }"
          :aria-pressed="chart.orientation === orientation"
          @click="set({ orientation })"
        >
          {{ t(`inspector.chart.orientations.${orientation}`) }}
        </button>
      </div>
    </InspectorField>

    <div v-if="shows('data')" class="grid">
      <div class="grid__head">
        <span class="b-label">{{ t('inspector.chart.data') }}</span>
        <div class="grid__ops">
          <button type="button" class="b-btn b-btn--outline" @click="addSeries">
            {{ t('inspector.chart.series') }}
          </button>
          <button type="button" class="b-btn b-btn--outline" @click="addLabel">
            {{ t('inspector.chart.label') }}
          </button>
        </div>
      </div>

      <div class="grid__scroll">
        <table class="grid__table">
          <thead>
            <tr>
              <th class="grid__corner" />
              <th v-for="(label, at) in chart.labels" :key="at" class="grid__column">
                <span class="grid__cell">
                  <input
                    v-if="byLabel"
                    class="well"
                    type="color"
                    :value="labelColour(chart, at, palette)"
                    :title="t('inspector.chart.labelColour')"
                    :aria-label="t('inspector.chart.labelColour')"
                    @input="setLabelColour(at, typed($event))"
                  />
                  <input
                    class="grid__input grid__input--head"
                    :value="label"
                    :placeholder="t('inspector.chart.labelPlaceholder', { number: at + 1 })"
                    @input="setLabel(at, typed($event))"
                  />
                  <button
                    type="button"
                    class="grid__mini grid__mini--drop"
                    :title="t('inspector.chart.removeLabel')"
                    :disabled="columns <= 1"
                    @click="removeLabel(at)"
                  >
                    &times;
                  </button>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(one, at) in chart.series" :key="one.id">
              <th class="grid__row-head">
                <input
                  v-if="!byLabel"
                  class="well"
                  type="color"
                  :value="seriesColour(chart, at, palette)"
                  :title="t('inspector.chart.seriesColour')"
                  :aria-label="t('inspector.chart.seriesColour')"
                  @input="setSeries(one.id, { colour: typed($event).toLowerCase() })"
                />
                <input
                  class="grid__input grid__input--series"
                  :value="one.label"
                  :placeholder="t('inspector.chart.seriesPlaceholder')"
                  @input="setSeries(one.id, { label: typed($event) })"
                />
                <button
                  type="button"
                  class="grid__mini grid__mini--drop"
                  :title="t('inspector.chart.removeSeries')"
                  :disabled="chart.series.length <= 1"
                  @click="removeSeries(one.id)"
                >
                  &times;
                </button>
              </th>
              <td v-for="(value, column) in one.values" :key="column">
                <input
                  class="grid__input grid__input--numeric"
                  inputmode="decimal"
                  :value="value"
                  @input="setValue(one.id, column, typed($event))"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="colours">
        <label v-if="canColourLabels" class="b-switch">
          <input type="checkbox" :checked="byLabel" @change="colourEachLabel(checked($event))" />
          <span class="b-switch__track" />
          <span class="switch-label">{{ t('inspector.chart.colourEach') }}</span>
        </label>
        <button
          v-if="customised"
          type="button"
          class="b-btn b-btn--soft colours__reset"
          @click="resetColours"
        >
          {{ t('inspector.chart.accentColours') }}
        </button>
      </div>
    </div>

    <div v-if="shows('look')" class="switches">
      <label v-if="bars && chart.series.length > 1" class="b-switch">
        <input
          type="checkbox"
          :checked="chart.stacked"
          @change="set({ stacked: checked($event) })"
        />
        <span class="b-switch__track" />
        <span class="switch-label">{{ t('inspector.chart.stacked') }}</span>
      </label>
      <label class="b-switch">
        <input
          type="checkbox"
          :checked="chart.showValues"
          @change="set({ showValues: checked($event) })"
        />
        <span class="b-switch__track" />
        <span class="switch-label">{{ t('inspector.chart.values') }}</span>
      </label>
      <label v-if="!round" class="b-switch">
        <input
          type="checkbox"
          :checked="chart.showAxis"
          @change="set({ showAxis: checked($event) })"
        />
        <span class="b-switch__track" />
        <span class="switch-label">{{ t('inspector.chart.axis') }}</span>
      </label>
      <label class="b-switch">
        <input
          type="checkbox"
          :checked="chart.showLegend"
          @change="set({ showLegend: checked($event) })"
        />
        <span class="b-switch__track" />
        <span class="switch-label">{{ t('inspector.chart.legend') }}</span>
      </label>
    </div>

    <InspectorField
      v-if="chart.showLegend && shows('look')"
      :label="t('inspector.chart.legendPosition')"
    >
      <div
        class="options options--5"
        role="group"
        :aria-label="t('inspector.chart.legendPosition')"
      >
        <button
          v-for="position in LEGEND_POSITIONS"
          :key="position"
          type="button"
          class="options__option"
          :class="{ 'options__option--on': chart.legendPosition === position }"
          :aria-pressed="chart.legendPosition === position"
          @click="set({ legendPosition: position })"
        >
          {{ t(`inspector.chart.legendPositions.${position}`) }}
        </button>
      </div>
    </InspectorField>

    <InspectorField
      v-if="shows('look')"
      :label="t('inspector.chart.unit')"
      :hint="t('inspector.chart.unitHint')"
    >
      <input
        class="b-input unit"
        :value="chart.unit"
        maxlength="12"
        @input="set({ unit: typed($event).trim() })"
      />
    </InspectorField>

    <InspectorField
      v-if="chart.chartType === 'doughnut' && shows('look')"
      :label="t('inspector.chart.centre')"
      :hint="t('inspector.chart.centreHint')"
    >
      <input
        class="b-input"
        :value="chart.centre"
        maxlength="60"
        @input="set({ centre: typed($event) })"
      />
    </InspectorField>
  </div>
</template>

<style scoped>
.fields {
  display: flex;
  flex-direction: column;
  gap: var(--sp-6);
}

.switches {
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}

.switch-label {
  font-size: var(--text-base);
}

.unit {
  max-width: 120px;
}

.options {
  display: grid;
  gap: 1px;
  padding: 2px;
  border: 1px solid var(--hairline-strong);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.options--4 {
  grid-template-columns: repeat(4, 1fr);
}

.options--2 {
  grid-template-columns: repeat(2, 1fr);
}

.options--5 {
  grid-template-columns: repeat(5, 1fr);
}

.options__option {
  height: 26px;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-soft);
  font: inherit;
  font-size: var(--text-xs);
  cursor: pointer;
}

.options__option:hover {
  color: var(--ink);
}

.options__option--on {
  background: var(--accent-quiet);
  color: var(--accent);
  font-weight: 600;
}

.grid__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--sp-4);
}

.grid__ops {
  display: flex;
  gap: var(--sp-3);
}

.grid__ops .b-btn {
  height: 24px;
  padding: 0 var(--sp-4);
  font-size: var(--text-sm);
}

.grid__scroll {
  overflow: auto;
  border: 1px solid var(--hairline);
  border-radius: var(--radius);
  background: var(--panel-raised);
}

.grid__table {
  border-collapse: collapse;
  width: 100%;
}

.grid__corner {
  width: 120px;
  padding: 0;
  background: var(--panel);
}

.grid__row-head {
  display: flex;
  align-items: center;
  width: 120px;
  padding: 0 0 0 var(--sp-3);
  background: var(--panel);
  border-top: 1px solid var(--hairline);
}

.grid__column {
  padding: 0;
  min-width: 84px;
  border-left: 1px solid var(--hairline);
  white-space: nowrap;
}

.grid__cell {
  display: flex;
  align-items: center;
  padding-left: var(--sp-3);
}

.grid__table td {
  padding: 0;
  border-top: 1px solid var(--hairline);
  border-left: 1px solid var(--hairline);
}

.grid__input {
  width: 100%;
  min-width: 0;
  padding: var(--sp-3) var(--sp-4);
  border: 0;
  background: transparent;
  color: var(--ink);
  font: inherit;
  font-size: var(--text-sm);
  font-variant-numeric: tabular-nums;
}

.grid__input:focus {
  outline: none;
  background: var(--accent-quiet);
}

.grid__input--head,
.grid__input--series {
  font-weight: 600;
}

.grid__input--numeric {
  text-align: right;
}

.grid__mini {
  height: 17px;
  padding: 0 4px;
  margin-right: 4px;
  border: 0;
  border-radius: var(--radius-xs);
  background: none;
  color: var(--ink-faint);
  font: inherit;
  font-size: 10px;
  cursor: pointer;
  flex: none;
}

.grid__mini:hover:not(:disabled) {
  color: var(--danger);
}

.grid__mini:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* A colour well as a small round swatch: the browser's picker opens on a click. */
.well {
  flex: none;
  width: 14px;
  height: 14px;
  padding: 0;
  border: 1px solid var(--hairline-strong);
  border-radius: 50%;
  background: none;
  cursor: pointer;
}

.well::-webkit-color-swatch-wrapper {
  padding: 0;
}

.well::-webkit-color-swatch {
  border: 0;
  border-radius: 50%;
}

.well::-moz-color-swatch {
  border: 0;
  border-radius: 50%;
}

.colours {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--sp-3);
  margin-top: var(--sp-4);
}

.colours__reset {
  height: 24px;
  padding: 0 var(--sp-4);
  font-size: var(--text-sm);
}
</style>
